import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import i18n from '../case-events/translate';

export type NotificationPreviewAttachment = {
  id: string;
  file_name: string;
};

export type NotificationPreviewDialogData = {
  subject: string;
  mediaUuid: string | null;
  attachments: NotificationPreviewAttachment[];
  routes: string[];
};

@Component({
  selector: 'payee-notification-preview-dialog',
  templateUrl: './notification-preview-dialog.component.html',
  styleUrls: ['./notification-preview-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeButtonComponent,
    PayeeIconComponent,
    MatDialogClose,
    MatProgressSpinner,
    TranslateModule,
  ],
})
export class NotificationPreviewDialogComponent implements OnInit, OnDestroy {
  private dialogRef =
    inject<MatDialogRef<NotificationPreviewDialogComponent>>(MatDialogRef);
  readonly data = inject<NotificationPreviewDialogData>(MAT_DIALOG_DATA);
  private sanitizer = inject(DomSanitizer);
  private translate = inject(TranslateService);

  private static translationsLoaded = false;

  readonly dialogWidth = 'min(95vw, 1280px)';
  previewUrl: SafeResourceUrl | null = null;
  previewLoading = false;

  private previewObjectUrl: string | null = null;

  constructor() {
    if (!NotificationPreviewDialogComponent.translationsLoaded) {
      for (const [lang, translation] of Object.entries(i18n)) {
        this.translate.setTranslation(lang, translation, true);
      }
      NotificationPreviewDialogComponent.translationsLoaded = true;
    }
  }

  get hasSidebar(): boolean {
    return this.data.attachments.length > 0 || this.data.routes.length > 0;
  }

  ngOnInit(): void {
    if (!this.data.mediaUuid) {
      this.onCancel();
      return;
    }

    void this.loadPreview();
  }

  ngOnDestroy(): void {
    this.revokePreviewObjectUrl();
  }

  private revokePreviewObjectUrl(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }

  private async loadPreview(): Promise<void> {
    const mediaUuid = this.data.mediaUuid;
    if (!mediaUuid) {
      return;
    }

    this.previewLoading = true;

    try {
      const response = await axios.get<Blob>(
        `${environment.baseUrl}/api/file/${mediaUuid}`,
        { responseType: 'blob' },
      );

      const contentType =
        typeof response.headers['content-type'] === 'string'
          ? response.headers['content-type']
          : 'text/html';
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: contentType });

      this.revokePreviewObjectUrl();
      this.previewObjectUrl = URL.createObjectURL(blob);
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.previewObjectUrl,
      );
    } catch {
      this.previewUrl = null;
    } finally {
      this.previewLoading = false;
    }
  }

  attachmentUrl(attachment: NotificationPreviewAttachment): string {
    return `${environment.baseUrl}/api/file/${attachment.id}`;
  }

  onCancel = (): void => {
    this.dialogRef.close();
  };
}
